import React, { useMemo } from "react";

import { useLocation } from "react-router";

import AudienceOverlapEmptyState, {
  AudienceOverlapLoadingState,
} from "src/app/components/AudienceOverlap/AudienceOverlapEmptyState";
import AudienceOverlapResult from "src/app/components/AudienceOverlap/AudienceOverlapResult";
import { AUDIENCE_OVERLAP_PROVIDERS } from "src/app/components/AudienceOverlap/constants";
import {
  AudienceOverlapSocialProfile,
  downloadCreatorsAudienceOverlapXlsx,
} from "src/app/components/AudienceOverlap/creatorsAudienceOverlapApi";
import {
  AudienceOverlapProviderResult,
  AudienceOverlapRow,
} from "src/app/components/AudienceOverlap/types";
import {
  CreatorsAudienceOverlapData,
  useCreatorsAudienceOverlap,
} from "src/app/components/AudienceOverlap/useCreatorsAudienceOverlap";
import { Button } from "src/app/components/Button/Button";
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from "src/app/components/Breadcrumbs/Breadcrumbs";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { Pane } from "src/app/components/Pane/Pane";
import {
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "src/app/components/Pane/PaneTopbar/PaneTopbar";
import {
  findElement,
  getAudienceOverlapSocialProfiles,
  hasEnoughSocialProfilesForAudienceOverlap,
} from "src/app/CreatorShowcase/components/ShowcaseAudienceOverlap/audienceOverlapUtils";
import { showToast } from "src/app/methods/showToast";
import { ReactComponent as XLSIcon } from "src/images/xlsx.svg";

import "src/app/pages/CreatorDiscoveryTool/legacy/app/audiences-manager/AudienceOverlapPane/AudienceOverlapPane.scss";
import "./CreatorSearchAudienceOverlapPane.scss";

export interface AudienceOverlapCreator {
  id: string;
  name: string;
  cover?: string | null;
  socialProfiles: { socialProvider: string; socialUsername: string }[];
}

interface Props {
  creators: AudienceOverlapCreator[];
  workspaceUuid: string;
  projectId?: string;
  onClose: () => void;
}

const NOT_ENOUGH_CREATORS = (
  <IDHFormattedMessage
    id="ws_audience_overlap_not_enough_creators"
    defaultMessage="Audience overlap needs at least two creators with an Instagram, TikTok or YouTube account on the same platform."
  />
);

const FAILURE_MESSAGES = {
  failed: (
    <IDHFormattedMessage
      id="ws_audience_overlap_not_enough_accounts_with_data"
      defaultMessage="Audience overlap needs at least two creators with audience data on the same platform."
    />
  ),
  timeout: (
    <IDHFormattedMessage
      id="ws_audience_overlap_takes_too_long"
      defaultMessage="The analysis is taking longer than expected. Please try again in a moment."
    />
  ),
  error: (
    <IDHFormattedMessage
      id="ws_audience_overlap_could_not_be_calculated"
      defaultMessage="We could not calculate audience overlap for the selected creators."
    />
  ),
};

const buildResults = (
  creators: AudienceOverlapCreator[],
  socialProfiles: AudienceOverlapSocialProfile[],
  data: CreatorsAudienceOverlapData,
): AudienceOverlapProviderResult[] =>
  // a tab is shown only for a platform on which any of the selected creators has an account
  AUDIENCE_OVERLAP_PROVIDERS.filter((provider) =>
    socialProfiles.some((profile) => profile.socialProvider === provider),
  ).map((provider) => {
    const providerProfiles = socialProfiles.filter(
      (profile) => profile.socialProvider === provider,
    );

    if (!hasEnoughSocialProfilesForAudienceOverlap(providerProfiles)) {
      return { provider, rows: [], message: NOT_ENOUGH_CREATORS };
    }

    const audienceOverlap = data[provider]?.audienceOverlap;
    const errors = Array.isArray(audienceOverlap?.errorsWithCalculatingOverlap)
      ? {}
      : (audienceOverlap?.errorsWithCalculatingOverlap as Record<
          string,
          string
        >) ?? {};

    if (!audienceOverlap?.data?.length && !Object.keys(errors).length) {
      return { provider, rows: [], message: FAILURE_MESSAGES.failed };
    }

    const toRow = (
      username: string,
      rest: Partial<AudienceOverlapRow>,
    ): AudienceOverlapRow => {
      const creator = findElement(creators, provider, username);

      return {
        key: `${provider}-${username}`,
        name: creator?.name || username,
        avatarUrl: creator?.cover,
        ...rest,
      };
    };

    return {
      provider,
      reportInfo: audienceOverlap?.reportInfo,
      rows: [
        ...[...(audienceOverlap?.data ?? [])]
          .sort((a, b) => a.username.localeCompare(b.username))
          .map((item) =>
            toRow(item.username, {
              followers: item.followers,
              uniquePercentage: item.uniquePercentage,
            }),
          ),
        ...Object.entries(errors).map(([username, errorMessage]) =>
          toRow(username, { errorMessage: String(errorMessage) }),
        ),
      ],
    };
  });

export default function CreatorSearchAudienceOverlapPane({
  creators,
  workspaceUuid,
  projectId,
  onClose,
}: Props) {
  const { pathname } = useLocation();

  // the panel shows the analysis of the selection it was opened with
  const socialProfiles = useMemo(
    () => getAudienceOverlapSocialProfiles(creators),
    [creators],
  );
  const request = {
    socialProfiles,
    workspaceUuid,
    wsProjectUuid: projectId,
  };
  const { status, data, retry } = useCreatorsAudienceOverlap(request);
  const results = useMemo(
    () => buildResults(creators, socialProfiles, data),
    [creators, socialProfiles, data],
  );
  const hasFigures = results.some((result) => result.reportInfo);

  const downloadXlsx = async () => {
    try {
      await downloadCreatorsAudienceOverlapXlsx(request);
    } catch (error) {
      showToast(
        "error",
        <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
        <IDHFormattedMessage
          id="ws_something_went_wrong"
          defaultMessage="Something went wrong!"
        />,
      );
    }
  };

  const renderContent = () => {
    if (status === "loading") return <AudienceOverlapLoadingState />;

    // The provider answered but could not count some accounts (e.g. hidden followers list).
    // It is a result, not an error: show the reason of every account instead of a generic failure.
    const hasAccountErrors = results.some((result) =>
      result.rows.some((row) => row.errorMessage),
    );

    if (status === "failed" && hasAccountErrors) {
      return <AudienceOverlapResult results={results} />;
    }

    if (status !== "done") {
      return (
        <AudienceOverlapEmptyState
          text={FAILURE_MESSAGES[status]}
          onRetry={retry}
        />
      );
    }

    return (
      <AudienceOverlapResult
        results={results}
        actions={
          hasFigures && (
            <Button
              size="medium"
              variant="white"
              className="creator-search-audience-overlap__download"
              onClick={downloadXlsx}
            >
              <XLSIcon />
              <IDHFormattedMessage
                id="ws_audience_overlap_download_in_xls"
                defaultMessage="Download in .XLS"
              />
            </Button>
          )
        }
      />
    );
  };

  return (
    <Pane showPane hidePane={onClose} width={798}>
      <PaneTopbar>
        <PaneTopbarLeft>
          <Breadcrumbs>
            <BreadcrumbsItem
              to={pathname}
              text={
                <IDHFormattedMessage
                  id="ws_creator_search"
                  defaultMessage="Creator Search"
                />
              }
            />
          </Breadcrumbs>
        </PaneTopbarLeft>
        <PaneTopbarToolbar hidePane={onClose} />
      </PaneTopbar>
      <div className="pane__container audience-overlap-pane creator-search-audience-overlap">
        <div className="audience-overlap-pane__title-bar">
          <h2 className="audience-overlap-pane__title">
            <IDHFormattedMessage
              id="front_audience_overlap_title"
              defaultMessage="Audience overlap"
            />
          </h2>
        </div>
        <p className="audience-overlap-pane__paragraph">
          <IDHFormattedMessage
            id="ws_creator_search_audience_overlap_description"
            defaultMessage="Percentage of overlapping and unique followers, both in number and percentage, across all followers of the selected creators. To analyze other creators, change the selection and click “Audience overlap” again."
          />
        </p>
        {renderContent()}
      </div>
    </Pane>
  );
}
